import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Typography, IconButton, Divider, LinearProgress, Button, Collapse, CircularProgress, Tooltip, TextField, Select, MenuItem, Menu, Popover } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import SpeedIcon from '@material-ui/icons/Speed';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import HotelIcon from '@material-ui/icons/Hotel';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ListIcon from '@material-ui/icons/List';
import TimelineIcon from '@material-ui/icons/Timeline';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import EuroSymbolIcon from '@material-ui/icons/EuroSymbol';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ReceiptIcon from '@material-ui/icons/Receipt';
import moment from 'moment';
import 'moment/locale/pt';
import { selectCurrentRoute, selectPreviewRoute, setRouteResult, addCustomCost, removeCustomCost, selectActualTrail } from '../../store/slices/routesSlice';
import { calculateStops } from '../../utils/tachograph';
import { analyzeTrail } from '../../utils/trailGeom';
import { FUEL_TYPES } from '../Sidebar/VehicleDriverForm';
import { selectVehicles } from '../../store/slices/vehiclesSlice';
import { selectDrivers } from '../../store/slices/driversSlice';
import { setShowDetails, setHighlightedStepIdx, selectUI } from '../../store/slices/uiSlice';
import { t } from '../../i18n';
import { getCurrency, fetchExchangeRates, getSavedFuelPrice } from '../../utils/currencies';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';
import TachographPanel from './TachographPanel';
import TollsPanel from './TollsPanel';
import FuelCountryPanel from './FuelCountryPanel';
import ModuleGate from '../common/ModuleGate';
import { selectTollData, selectTollLoading, fetchTollData, selectTollCountryOverrides } from '../../store/slices/tollsSlice';
import { selectFuelPrices, selectFuelPriceOverrides, selectFuelPricesLoading, fetchFuelPrices, clearFuelPriceOverrides } from '../../store/slices/fuelPricesSlice';
import { AuthContext } from '../../App';
import { exportRoutePdf, exportDriverPdf } from '../../utils/exportPdf';
import RouteSummaryDialog from './RouteSummaryDialog';
import DriverPdfPreviewDialog from '../common/DriverPdfPreviewDialog';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import PersonIcon from '@material-ui/icons/Person';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import { resolveFuelPriceEUR } from '../../utils/resolveFuelPrice';

moment.locale('pt');

const useStyles = makeStyles(() => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
  },
  metricCard: {
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  metricLabel: {
    fontSize: '0.6rem',
    fontWeight: 600,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: '#94a3b8',
  },
  metricValue: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#0f172a',
    lineHeight: 1.1,
  },
  metricUnit: {
    fontSize: '0.65rem',
    color: '#64748b',
  },
  statusBar: {
    padding: '10px 12px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
}));

function MetricCard({ label, value, unit, icon: Icon, iconColor, sub, onClick, badge }) {
  const classes = useStyles();
  const clickable = typeof onClick === 'function';
  return (
    <GlassCard
      className={classes.metricCard}
      flat
      onClick={clickable ? onClick : undefined}
      style={clickable ? { cursor: 'pointer', position: 'relative' } : undefined}
      title={clickable ? 'Clica para ver detalhe' : undefined}
    >
      {badge && (
        <Box style={{
          position: 'absolute', top: 4, right: 4,
          width: 16, height: 16, borderRadius: 10,
          background: '#3b82f6', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 700,
        }}>›</Box>
      )}
      <Box style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        {Icon && <Icon style={{ fontSize: 12, color: iconColor || '#94a3b8' }} />}
        <Typography className={classes.metricLabel}>{label}</Typography>
      </Box>
      <Typography className={classes.metricValue}>{value ?? '—'}</Typography>
      {unit && <Typography className={classes.metricUnit}>{unit}</Typography>}
      {sub && <Typography style={{ fontSize: '0.55rem', color: '#64748b', marginTop: 2 }}>{sub}</Typography>}
    </GlassCard>
  );
}

const ROAD_LABEL_KEYS = {
  motorway: { key: 'roadMotorway', color: '#f59e0b' },
  highway:  { key: 'roadHighway',  color: '#8b5cf6' },
  national: { key: 'roadNational', color: '#ef4444' },
  local:    { key: 'roadLocal',    color: '#3b82f6' },
};

function RouteDetails() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings, modules } = useContext(AuthContext);
  // Portagens só se o módulo 'european_tolls' estiver activo (fail-open sem
  // lista). Em Angola (Move Express) o módulo está OFF → não se buscam
  // portagens (não existem) e poupam-se chamadas repetidas à rede.
  const tollsEnabled = !modules || modules.length === 0 || modules.includes('european_tolls');
  const [showSteps, setShowSteps] = useState(false);
  const [showDriverPdfPreview, setShowDriverPdfPreview] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [durationMenuAnchor, setDurationMenuAnchor] = useState(null);
  const [fxData, setFxData] = useState(null); // { rates, live, cached } | null
  const [showCostForm, setShowCostForm] = useState(false);
  const [costName, setCostName] = useState('');
  const [costValue, setCostValue] = useState('');
  const [costTemplate, setCostTemplate] = useState('');

  // Fetch live exchange rates on mount (cached 1h in localStorage)
  useEffect(() => {
    fetchExchangeRates().then(data => { if (data) setFxData(data); });
  }, []);

  const route = useSelector(selectCurrentRoute);
  const previewRoute = useSelector(selectPreviewRoute);
  const vehicles = useSelector(selectVehicles);
  const drivers = useSelector(selectDrivers);
  const { lang, highlightedStepIdx } = useSelector(selectUI);

  const actualTrail = useSelector(selectActualTrail);
  const tollData = useSelector(selectTollData);
  const tollOverrides = useSelector(selectTollCountryOverrides);
  const tollLoading = useSelector(selectTollLoading);
  const fuelPrices = useSelector(selectFuelPrices);
  const fuelOverrides = useSelector(selectFuelPriceOverrides);
  const fuelPricesLoading = useSelector(selectFuelPricesLoading);

  // Re-fetch fuel prices when fuelType changes (and route has country data)
  const fuelType = route.fuelType || 'diesel';
  const activeRoute = previewRoute || route.computedRoute;
  const countryDistancesKeys = activeRoute?.countryDistances
    ? Object.keys(activeRoute.countryDistances)
    : [];
  useEffect(() => {
    if (countryDistancesKeys.length > 0 && token) {
      dispatch(clearFuelPriceOverrides());
      dispatch(fetchFuelPrices({ token, countries: countryDistancesKeys, fuelType }));
    }
  }, [fuelType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch toll data when route or options change
  const vehicleType = route.routeOptions?.vehicleType || 'hgv';
  const opts = route.routeOptions || {};
  const optsKey = JSON.stringify({ avoidTolls: opts.avoidTolls, avoidMotorways: opts.avoidMotorways, avoidFerries: opts.avoidFerries, truckWeight: opts.truckWeight, truckHeight: opts.truckHeight, truckWidth: opts.truckWidth, truckLength: opts.truckLength });
  useEffect(() => {
    // Sem módulo de portagens (ex.: Angola) não há portagens para calcular —
    // evita chamadas desnecessárias (e repetidas) à rede.
    if (!tollsEnabled) return;
    const currentOpts = route.routeOptions || {};
    const transportMode = vehicleType === 'car' ? 'car' : 'truck';
    if (token && route.origin?.coordinates && route.destination?.coordinates && route.computedRoute) {
      dispatch(fetchTollData({
        token,
        origin: { lat: route.origin.coordinates[1], lng: route.origin.coordinates[0] },
        destination: { lat: route.destination.coordinates[1], lng: route.destination.coordinates[0] },
        waypoints: route.waypoints || [],
        transportMode,
        routeOptions: currentOpts,
      }));
    }
  }, [vehicleType, route.computedRoute, optsKey, tollsEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const vehicle = vehicles.find(v => v.id === route.vehicleId);
  const driver = drivers.find(d => d.id === route.driverId);
  const cr = previewRoute || route.computedRoute;
  const isPreview = !!previewRoute;

  const tripMultiplier = (route.roundTrip && !isPreview) ? 2 : 1;
  const distanceKm = cr ? (cr.distance / 1000) * tripMultiplier : 0;
  const durationMin = cr ? Math.round(cr.duration / 60) * tripMultiplier : 0;
  const durationH = Math.floor(durationMin / 60);
  const durationM = durationMin % 60;
  const durationD = Math.floor(durationH / 24);
  const durationHRem = durationH % 24;
  const durationLabel = durationD > 0
    ? `${durationD}d ${durationHRem}h ${durationM}m`
    : `${durationH}h ${durationM}m`;

  const currency = getCurrency(route.routeOptions?.currency, fxData?.rates);
  const cur = currency.symbol;
  const isLive = fxData?.live && currency.code !== 'EUR';
  // conv(eurValue) — converts from EUR base to selected currency, formatted appropriately
  const conv = (eur, decimals) => {
    const val = eur * currency.rate;
    const dp = decimals !== undefined ? decimals : (currency.rate >= 100 ? 0 : 2);
    return val.toLocaleString('pt-PT', { minimumFractionDigits: dp, maximumFractionDigits: dp });
  };

  const fuelDef = FUEL_TYPES.find(f => f.value === fuelType) || FUEL_TYPES[0];
  // Car consumption is roughly 35-40% of truck consumption
  const defaultConsumption = vehicleType === 'car'
    ? Math.round(fuelDef.consumptionDefault * 0.38)
    : fuelDef.consumptionDefault;
  const consumption = route.fuelConsumption ?? vehicle?.fuelPer100km ?? defaultConsumption;
  // route.fuelPrice is stored in the display currency (EUR/AOA/etc).
  // If not set, fall back to the country-specific default (AOA diesel = 400 Kz, EUR = 1.55€).
  const pricePerUnit = route.fuelPrice ?? getSavedFuelPrice(currency.code, fuelType);
  // Normalise to EUR so conv() (which multiplies by currency.rate) gives the right local value
  const pricePerUnitEUR = pricePerUnit / currency.rate;
  const totalFuel = distanceKm ? (distanceKm / 100) * consumption : 0;
  const totalFuelCost = totalFuel * pricePerUnitEUR;
  const costPerKm = distanceKm ? totalFuelCost / distanceKm : 0;
  const co2Kg = fuelDef.co2PerUnit > 0 ? totalFuel * fuelDef.co2PerUnit : 0;

  // Combustível em EUR — conta AUTORITÁRIA: preço por país (resolveFuelPriceEUR)
  // com tripMultiplier, exactamente como o card "Custo total da viagem" e o
  // FuelCountryPanel. Fallback para totalFuelCost (preço único) enquanto não há
  // dados por país. É este valor — e não o totalFuelCost simples — que alimenta
  // o resumo de viagem, senão os dois divergem (ex.: 50 637 vs 60 186 Kz).
  const effectiveFuelCostEUR = useMemo(() => {
    const countryDistances = cr?.countryDistances;
    const hasCountryData = countryDistances && Object.keys(countryDistances).length > 0;
    if (!hasCountryData || fuelPricesLoading) return totalFuelCost;
    const entries = Object.entries(countryDistances);
    const singleCountry = entries.length === 1;
    let sum = 0;
    for (const [cc, distM] of entries) {
      const distKm = (distM / 1000) * tripMultiplier;
      const fuel = (distKm / 100) * consumption;
      const price = resolveFuelPriceEUR(cc, fuelType, {
        overrides: fuelOverrides, clientSettings, route, fuelPrices,
        routeRate: currency.rate, fuelDef, singleCountry,
      });
      sum += fuel * price;
    }
    return sum;
  }, [cr, fuelPricesLoading, totalFuelCost, tripMultiplier, consumption, fuelType, fuelOverrides, clientSettings, route, fuelPrices, currency.rate, fuelDef]);

  // "Usa App" deriva da config de Ordens (orderFlows[t].usesApp) — igual ao
  // Header. Se nenhum tipo de OT usa a App, o "PDF Motorista" (pré-visualização
  // no telemóvel) não faz sentido: não há App onde a rota apareça. Fail-open
  // quando não há config.
  const usesDriverApp = (() => {
    const flows = clientSettings?.orderFlows;
    if (!flows || Object.keys(flows).length === 0) return true;
    return Object.values(flows).some(f => f?.usesApp !== false);
  })();

  const totalStopMin = (route.stops || []).reduce((s, st) => s + (st.duration || 0), 0);
  const wpStopMin = (route.waypoints || []).reduce((s, wp) => s + (wp.stopDuration || 0), 0);
  // Mercadoria/Casais: a "Duração" mostrada inclui os tempos de PARAGEM (tempo
  // real da rota), não só a condução. durationMin (condução) mantém-se para a
  // Chegada e o limite diário. Gated à variante mercadoria.
  const isMercadoria = clientSettings?.uiVariant === 'mercadoria';
  const displayDurationMin = isMercadoria ? (durationMin + totalStopMin + wpStopMin) : durationMin;
  const _ddH = Math.floor(displayDurationMin / 60);
  const _ddM = displayDurationMin % 60;
  const _ddD = Math.floor(_ddH / 24);
  const displayDurationLabel = _ddD > 0
    ? `${_ddD}d ${_ddH % 24}h ${_ddM}m`
    : `${_ddH}h ${_ddM}m`;
  const arrivalMoment = route.departureTime && cr
    ? moment(route.departureTime).add(durationMin + totalStopMin + wpStopMin, 'minutes')
    : null;
  const arrivalTime = arrivalMoment ? arrivalMoment.format('DD/MM HH:mm') : null;

  // Check if arrival is within the time window
  const atw = route.arrivalTimeWindow;
  let arrivalStatus = null; // null = no window set, 'on_time' | 'early' | 'late'
  if (arrivalMoment && atw) {
    const atwStart = atw.start ? moment(atw.start) : null;
    const atwEnd = atw.end ? moment(atw.end) : null;
    if (atwStart && atwEnd) {
      if (arrivalMoment.isBefore(atwStart)) arrivalStatus = 'early';
      else if (arrivalMoment.isAfter(atwEnd)) arrivalStatus = 'late';
      else arrivalStatus = 'on_time';
    } else if (atwEnd) {
      arrivalStatus = arrivalMoment.isAfter(atwEnd) ? 'late' : 'on_time';
    } else if (atwStart) {
      arrivalStatus = arrivalMoment.isBefore(atwStart) ? 'early' : 'on_time';
    }
  }

  const dailyLimitPct = Math.min(100, (durationMin / 540) * 100);
  const exceedsDaily = durationMin > 540;

  return (
    <Box className={classes.root}>
      {/* Banner amarelo quando a rota tem alterações por gravar (ex:
          adicionar waypoint, mudar origem) mas a geometria mostrada
          em baixo é a antiga. Sem isto o painel mostrava troços/km
          desactualizados sem dar pista ao operador. */}
      {!isPreview && route?.modifiedSinceLoad && cr && (
        <Box style={{
          padding: '8px 14px',
          background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
          borderBottom: '1px solid #fcd34d',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Box style={{ fontSize: 14 }}>⚠</Box>
          <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#92400e', flex: 1, lineHeight: 1.35 }}>
            {lang === 'pt'
              ? 'A rota foi alterada — os troços e métricas abaixo são da versão anterior. Clique "Calcular Rota" no painel esquerdo para actualizar.'
              : 'Route was modified — the segments and metrics below are from the previous version. Click "Calculate Route" on the left panel to refresh.'}
          </Typography>
        </Box>
      )}
      {isPreview && (
        <Box style={{ background: `linear-gradient(135deg, ${previewRoute.color || '#3b82f6'}20 0%, ${previewRoute.color || '#3b82f6'}08 100%)`, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6, borderBottom: `2px solid ${previewRoute.color || '#3b82f6'}` }}>
          <Box style={{ width: 8, height: 8, borderRadius: '50%', background: previewRoute.color || '#3b82f6' }} />
          <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: previewRoute.color || '#3b82f6' }}>
            {previewRoute.label || 'Preview'}
          </Typography>
          {previewRoute.totalCost != null && (
            <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#64748b', marginLeft: 'auto' }}>
              {lang === 'pt' ? 'Custo total' : 'Total cost'}: {(previewRoute.totalCost || 0).toFixed(2)} €
            </Typography>
          )}
        </Box>
      )}
      <Box className={classes.header}>
        <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0f172a' }}>
          {isPreview ? (previewRoute.label || t(lang, 'routeDetails')) : t(lang, 'routeDetails')}
        </Typography>
        {cr && (() => {
          const anyVehicleSimulating = vehicles.some(v => v._simulating);
          return (<>
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
              endIcon={<MoreVertIcon style={{ fontSize: 16 }} />}
              style={{
                fontSize: '0.7rem', fontWeight: 700, textTransform: 'none',
                borderColor: actionsMenuAnchor ? '#c7d2fe' : '#e2e8f0',
                color: actionsMenuAnchor ? '#4338ca' : '#475569',
                background: actionsMenuAnchor ? '#eef2ff' : '#fff',
                borderRadius: 10, padding: '4px 10px', minWidth: 0,
                boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
              }}
            >
              {lang === 'pt' ? 'Ações' : 'Actions'}
              {anyVehicleSimulating && (
                <Box style={{
                  marginLeft: 6, width: 7, height: 7, borderRadius: '50%',
                  background: '#ef4444',
                  boxShadow: '0 0 0 3px rgba(239,68,68,0.25)',
                  animation: 'pulse-dot 1.4s ease-in-out infinite',
                }} />
              )}
            </Button>
            <style>{`@keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
            <Menu
              anchorEl={actionsMenuAnchor}
              open={Boolean(actionsMenuAnchor)}
              onClose={() => setActionsMenuAnchor(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                style: {
                  marginTop: 6, borderRadius: 12, minWidth: 240,
                  boxShadow: '0 12px 32px rgba(15,23,42,0.12), 0 0 0 1px rgba(15,23,42,0.04)',
                  padding: 4,
                },
              }}
            >
              <Box style={{ padding: '6px 10px 8px', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {lang === 'pt' ? 'Ações da rota' : 'Route actions'}
                </Typography>
              </Box>
              <MenuItem
                onClick={() => {
                  setSummaryDialogOpen(true);
                  setActionsMenuAnchor(null);
                }}
                style={{ borderRadius: 8, padding: '8px 10px', gap: 10 }}
              >
                <Box style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(135deg, #6366f1, #4338ca)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 14,
                }}>
                  📄
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a' }}>
                    {lang === 'pt' ? 'Resumo da viagem' : 'Trip summary'}
                  </Typography>
                  <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                    {lang === 'pt' ? 'Itinerário visual + PDF para cliente' : 'Visual itinerary + PDF for customer'}
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  exportRoutePdf(route, vehicle?.plate || route.vehiclePlate, driver?.name || route.driverName);
                  setActionsMenuAnchor(null);
                }}
                style={{ borderRadius: 8, padding: '8px 10px', gap: 10 }}
              >
                <Box style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PictureAsPdfIcon style={{ fontSize: 14, color: '#64748b' }} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a' }}>
                    {lang === 'pt' ? 'PDF completo' : 'Full PDF'}
                  </Typography>
                  <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                    {lang === 'pt' ? 'Inclui custos e detalhes operacionais' : 'Includes costs and operational details'}
                  </Typography>
                </Box>
              </MenuItem>
              {/* PDF Motorista (pré-visualização no telemóvel) — só faz sentido
                  se algum tipo de OT usa a App. Sem App, esconde-se. */}
              {usesDriverApp && (
              <MenuItem
                onClick={() => {
                  setShowDriverPdfPreview(true);
                  setActionsMenuAnchor(null);
                }}
                style={{ borderRadius: 8, padding: '8px 10px', gap: 10 }}
              >
                <Box style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PersonIcon style={{ fontSize: 14, color: '#1d4ed8' }} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a' }}>
                    {lang === 'pt' ? 'PDF Motorista' : 'Driver PDF'}
                  </Typography>
                  <Typography style={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                    {lang === 'pt' ? 'Pré-visualização no telemóvel' : 'Phone mockup preview'}
                  </Typography>
                </Box>
              </MenuItem>
              )}
              {/* Simulation controls removed — operator now monitors real
                  GPS only (driver app + Traccar). The /simulate endpoints
                  remain on the backend for emergency/demo use, but no UI
                  surfaces them anymore. */}
            </Menu>
            <DriverPdfPreviewDialog
              open={showDriverPdfPreview}
              route={route}
              vehiclePlate={vehicle?.plate || route.vehiclePlate}
              driverName={driver?.name || route.driverName}
              lang={lang}
              onClose={() => setShowDriverPdfPreview(false)}
            />
          </>);
        })()}
      </Box>

      {route.name && (
        <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: -8 }}>
          {route.name}
          {route.cargoRef && <span style={{ color: '#94a3b8' }}> · {route.cargoRef}</span>}
        </Typography>
      )}

      {/* Main metrics */}
      <Box className={classes.metricGrid}>
        <MetricCard
          label={t(lang, 'distance')}
          value={distanceKm.toFixed(1)}
          unit="km"
          icon={SpeedIcon}
          iconColor="#3b82f6"
        />
        <MetricCard
          label={t(lang, 'duration')}
          value={displayDurationLabel}
          icon={AccessTimeIcon}
          iconColor="#6366f1"
          sub={isMercadoria && (totalStopMin + wpStopMin) > 0
            ? `Condução ${durationLabel}${arrivalTime ? ` · Chegada: ${arrivalTime}` : ''}`
            : (arrivalTime ? `Chegada: ${arrivalTime}` : (route.departureTime ? null : 'Define a Partida para ver a chegada'))}
          badge
          onClick={(e) => setDurationMenuAnchor(e.currentTarget)}
        />
        <MetricCard
          label={`${t(lang, 'fuel')} (${fuelDef.unit})`}
          value={distanceKm ? totalFuel.toFixed(1) : '—'}
          unit={fuelDef.unit}
          icon={LocalGasStationIcon}
          iconColor="#f59e0b"
        />
        {route.routeOptions?.useTachograph !== false && (
          <MetricCard
            label={t(lang, 'stops')}
            value={route.stops.length}
            icon={HotelIcon}
            iconColor="#22c55e"
          />
        )}
      </Box>

      {/* Duration breakdown popover — opens when the operator clicks the
          "Duração" metric card. Shows the timeline of each stop with
          arrival / departure times, so you can see how each waypoint
          shifts the schedule. Needs route.departureTime to be useful. */}
      <Popover
        open={Boolean(durationMenuAnchor)}
        anchorEl={durationMenuAnchor}
        onClose={() => setDurationMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ style: { marginTop: 6, borderRadius: 12, padding: 14, minWidth: 320, maxWidth: 440, boxShadow: '0 16px 40px rgba(15,23,42,0.15)' } }}
      >
        {(() => {
          const dep = route.departureTime ? moment(route.departureTime) : null;
          const legs = cr?.legs || cr?.sections || [];
          // If no departureTime, show a short tutorial instead
          if (!dep) {
            return (
              <Box>
                <Typography style={{ fontSize: '0.85rem', fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                  ⏱ Timeline da rota
                </Typography>
                <Typography style={{ fontSize: '0.74rem', color: '#64748b', lineHeight: 1.5 }}>
                  Define uma <b>data/hora de partida</b> no painel esquerdo ("Veículo &amp; Horário")
                  para ver aqui a hora de chegada a cada ponto intermédio e ao destino.
                </Typography>
                <Box style={{ marginTop: 10, padding: '8px 10px', background: '#f8fafc', borderRadius: 8, border: '1px dashed #cbd5e1' }}>
                  <Typography style={{ fontSize: '0.7rem', color: '#475569' }}>
                    <b>Duração total (condução):</b> {durationLabel}<br />
                    <b>Com paragens:</b> {durationH}h {durationM + totalStopMin + wpStopMin}m<br />
                    <b>Distância:</b> {distanceKm.toFixed(1)} km
                  </Typography>
                </Box>
              </Box>
            );
          }

          // Build the stop sequence: origin → waypoints (with legs if available) → destination
          const stops = [];
          let t = dep.clone();
          stops.push({
            type: 'origin',
            name: route.origin?.name || 'Partida',
            arrival: null,
            depart: t.clone(),
            stopMin: 0,
          });
          // If HERE returned per-leg durations, use them. Otherwise distribute proportionally.
          const wps = route.waypoints || [];
          const N = wps.length + 1; // legs = waypoints + 1 (to destination)
          const perLegSec = legs.length === N
            ? legs.map(l => l.travelSummary?.duration || l.summary?.duration || l.duration || 0)
            : Array(N).fill(cr.duration / N);

          wps.forEach((wp, idx) => {
            const legSec = perLegSec[idx] || 0;
            t = t.clone().add(legSec, 'seconds');
            const arrival = t.clone();
            const stopMin = wp.stopDuration || 0;
            t = t.clone().add(stopMin, 'minutes');
            stops.push({
              type: 'waypoint',
              index: idx + 1,
              name: wp.name || `Ponto ${idx + 1}`,
              arrival,
              depart: t.clone(),
              stopMin,
            });
          });
          // Final leg to destination
          const finalLegSec = perLegSec[perLegSec.length - 1] || 0;
          t = t.clone().add(finalLegSec, 'seconds');
          stops.push({
            type: 'destination',
            name: route.destination?.name || 'Chegada',
            arrival: t.clone(),
            depart: null,
            stopMin: 0,
          });

          return (
            <Box>
              <Box style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                <Typography style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>⏱ Timeline da rota</Typography>
                <Typography style={{ fontSize: '0.66rem', color: '#64748b' }}>
                  {durationLabel} condução + {totalStopMin + wpStopMin}min paragens
                </Typography>
              </Box>
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {stops.map((s, i) => {
                  const color = s.type === 'origin' ? '#16a34a' : s.type === 'destination' ? '#dc2626' : '#f59e0b';
                  const label = s.type === 'origin'
                    ? 'Partida'
                    : s.type === 'destination'
                      ? 'Chegada'
                      : `Ponto ${s.index}`;
                  return (
                    <Box key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0' }}>
                      {/* Dot + connecting line */}
                      <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                        <Box style={{
                          width: 10, height: 10, borderRadius: '50%',
                          background: color, border: '2px solid #fff',
                          boxShadow: `0 0 0 2px ${color}33`,
                        }} />
                        {i < stops.length - 1 && (
                          <Box style={{ width: 2, flex: 1, minHeight: 14, background: '#e2e8f0', marginTop: 2 }} />
                        )}
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{ fontSize: '0.6rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {label}
                        </Typography>
                        <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.name}
                        </Typography>
                        <Typography style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 2 }}>
                          {s.arrival && <>🕐 Chega {s.arrival.format('HH:mm')}</>}
                          {s.arrival && s.depart && <span style={{ color: '#cbd5e1', margin: '0 6px' }}>·</span>}
                          {s.stopMin > 0 && <>⏸ {s.stopMin}min paragem</>}
                          {s.stopMin > 0 && s.depart && <span style={{ color: '#cbd5e1', margin: '0 6px' }}>·</span>}
                          {s.depart && s.type !== 'destination' && <>🚗 Sai {s.depart.format('HH:mm')}</>}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })()}
      </Popover>

      {/* Arrival time + deadline status */}
      {arrivalTime && (
        <GlassCard padding="sm" flat style={{
          borderLeft: `3px solid ${arrivalStatus === 'on_time' ? '#22c55e' : arrivalStatus === 'late' || arrivalStatus === 'early' ? '#ef4444' : '#3b82f6'}`,
        }}>
          <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
            {t(lang, 'arrivalTime')} {t(lang, 'withStops')}
          </Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Typography style={{ fontSize: '1.15rem', fontWeight: 700, color: arrivalStatus === 'on_time' ? '#22c55e' : arrivalStatus ? '#ef4444' : '#3b82f6' }}>
              {arrivalTime}
            </Typography>
            {arrivalStatus && (
              <Box style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                background: arrivalStatus === 'on_time' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                border: `1px solid ${arrivalStatus === 'on_time' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                borderRadius: 20, padding: '2px 8px',
              }}>
                {arrivalStatus === 'on_time'
                  ? <CheckCircleIcon style={{ fontSize: 12, color: '#22c55e' }} />
                  : <WarningIcon style={{ fontSize: 12, color: '#ef4444' }} />
                }
                <Typography style={{
                  fontSize: '0.58rem', fontWeight: 700, whiteSpace: 'nowrap',
                  color: arrivalStatus === 'on_time' ? '#22c55e' : '#ef4444',
                }}>
                  {t(lang, arrivalStatus === 'on_time' ? 'onTime' : arrivalStatus === 'early' ? 'tooEarly' : 'tooLate')}
                </Typography>
              </Box>
            )}
          </Box>
          {atw && (atw.start || atw.end) && (
            <Typography style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: 3 }}>
              {t(lang, 'timeWindow')}: {atw.start ? moment(atw.start).format('DD/MM HH:mm') : '—'} → {atw.end ? moment(atw.end).format('DD/MM HH:mm') : '—'}
            </Typography>
          )}
        </GlassCard>
      )}

      {/* EU 561/2006 daily driving limit — only relevant when a tachograph is in use.
          Hide it for cars and when the tenant/route disables the tachograph. */}
      {route.routeOptions?.useTachograph !== false
        && route.routeOptions?.vehicleType !== 'car'
        && clientSettings?.useTachograph !== false && (
        <Box>
          <SectionTitle>{t(lang, 'euDailyLimit')}</SectionTitle>
          <GlassCard padding="sm" flat>
            <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <Typography style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                {durationH}h {durationM}min / 9h
              </Typography>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {exceedsDaily ? (
                  <WarningIcon style={{ fontSize: 12, color: '#ef4444' }} />
                ) : (
                  <CheckCircleIcon style={{ fontSize: 12, color: '#22c55e' }} />
                )}
                <Typography style={{ fontSize: '0.7rem', color: exceedsDaily ? '#ef4444' : '#22c55e', fontWeight: 600 }}>
                  {exceedsDaily ? t(lang, 'exceedsLimit') : t(lang, 'withinLimit')}
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={dailyLimitPct}
              style={{ height: 4, borderRadius: 2 }}
              color={exceedsDaily ? 'secondary' : 'primary'}
            />
          </GlassCard>
        </Box>
      )}

      {/* Vehicle type & fuel */}
      <Box>
        <SectionTitle>{lang === 'pt' ? 'Veículo & Combustível' : 'Vehicle & Fuel'}</SectionTitle>
        <GlassCard padding="sm" flat>
          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#3b82f6' }}>
                {(() => {
                  const sub = route.routeOptions?.vehicleSubType;
                  const labels = {
                    cisterna: lang === 'pt' ? 'Cisterna (40t)' : 'Tanker (40t)',
                    frigorifico: lang === 'pt' ? 'Frigorífico (26t)' : 'Reefer (26t)',
                    light_truck: lang === 'pt' ? 'Ligeiro (7.5t)' : 'Light Truck (7.5t)',
                    general_cargo: lang === 'pt' ? 'Mercadorias (24t)' : 'General Cargo (24t)',
                    car: lang === 'pt' ? 'Ligeiro' : 'Car',
                  };
                  return labels[sub] || (vehicleType === 'car' ? labels.car : (lang === 'pt' ? 'Pesado' : 'Truck'));
                })()}
              </Typography>
            </Box>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#fefce8', border: '1px solid #fef08a' }}>
              <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#ca8a04' }}>
                {(route.fuelType || 'diesel').charAt(0).toUpperCase() + (route.fuelType || 'diesel').slice(1)}
              </Typography>
            </Box>
            {route.fuelConsumption && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#16a34a' }}>
                  {route.fuelConsumption} L/100km
                </Typography>
              </Box>
            )}
            {route.vehiclePlate && (
              <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#475569' }}>
                  {route.vehiclePlate}
                </Typography>
              </Box>
            )}
          </Box>
        </GlassCard>
      </Box>

      {/* Route conditions */}
      {(opts.avoidTolls || opts.avoidFerries || opts.avoidMotorways || opts.avoidTunnels || opts.avoidUnpaved || (opts.avoidUrbanAreas || []).length > 0 || opts.truckWeight || opts.truckHeight) && (
        <Box>
          <SectionTitle>{lang === 'pt' ? 'Condições da Rota' : 'Route Conditions'}</SectionTitle>
          <GlassCard padding="sm" flat>
            <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {opts.avoidTolls && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#ef4444' }}>
                    {lang === 'pt' ? 'Sem Portagens' : 'No Tolls'}
                  </Typography>
                </Box>
              )}
              {opts.avoidMotorways && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#fff7ed', border: '1px solid #fed7aa' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#f97316' }}>
                    {lang === 'pt' ? 'Sem Autoestradas' : 'No Motorways'}
                  </Typography>
                </Box>
              )}
              {opts.avoidFerries && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#3b82f6' }}>
                    {lang === 'pt' ? 'Sem Ferries' : 'No Ferries'}
                  </Typography>
                </Box>
              )}
              {opts.avoidTunnels && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#f5f3ff', border: '1px solid #ddd6fe' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#8b5cf6' }}>
                    {lang === 'pt' ? 'Sem Túneis' : 'No Tunnels'}
                  </Typography>
                </Box>
              )}
              {opts.avoidUnpaved && (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#fefce8', border: '1px solid #fef08a' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#ca8a04' }}>
                    {lang === 'pt' ? 'Sem Estradas Não Pavimentadas' : 'No Unpaved Roads'}
                  </Typography>
                </Box>
              )}
              {(opts.avoidUrbanAreas || []).map(city => (
                <Box key={city} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 6, background: '#fdf2f8', border: '1px solid #fbcfe8' }}>
                  <Typography style={{ fontSize: '0.62rem', fontWeight: 600, color: '#ec4899' }}>
                    {lang === 'pt' ? `Evitar ${city}` : `Avoid ${city}`}
                  </Typography>
                </Box>
              ))}
            </Box>
            {/* Vehicle dimensions if set */}
            {(opts.truckWeight || opts.truckHeight || opts.truckWidth || opts.truckLength || opts.truckAxleLoad) && (
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6, paddingTop: 6, borderTop: '1px solid #f1f5f9' }}>
                {opts.truckWeight && (
                  <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>
                    {lang === 'pt' ? 'Peso' : 'Weight'}: <strong>{opts.truckWeight}t</strong>
                  </Typography>
                )}
                {opts.truckAxleLoad && (
                  <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>
                    {lang === 'pt' ? 'Eixo' : 'Axle'}: <strong>{opts.truckAxleLoad}t</strong>
                  </Typography>
                )}
                {opts.truckHeight && (
                  <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>
                    {lang === 'pt' ? 'Altura' : 'Height'}: <strong>{opts.truckHeight}m</strong>
                  </Typography>
                )}
                {opts.truckWidth && (
                  <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>
                    {lang === 'pt' ? 'Largura' : 'Width'}: <strong>{opts.truckWidth}m</strong>
                  </Typography>
                )}
                {opts.truckLength && (
                  <Typography style={{ fontSize: '0.6rem', color: '#64748b' }}>
                    {lang === 'pt' ? 'Comprimento' : 'Length'}: <strong>{opts.truckLength}m</strong>
                  </Typography>
                )}
              </Box>
            )}
          </GlassCard>
        </Box>
      )}

      {/* Fuel cost per country (if multi-country route) */}
      {distanceKm > 0 && cr?.countryDistances && Object.keys(cr.countryDistances).length > 0 && (
        <FuelCountryPanel lang={lang} currency={currency} tripMultiplier={tripMultiplier} />
      )}

      {/* Fuel cost breakdown (fallback for single-country or no country data) */}
      {distanceKm > 0 && (!cr?.countryDistances || Object.keys(cr.countryDistances).length === 0) && (
        <Box>
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <SectionTitle icon={LocalGasStationIcon}>{t(lang, 'fuelCosts')}</SectionTitle>
            {currency.code !== 'EUR' && (
              <Box style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: isLive ? 'rgba(34,197,94,0.1)' : 'rgba(245,158,11,0.08)',
                border: `1px solid ${isLive ? 'rgba(34,197,94,0.35)' : 'rgba(245,158,11,0.35)'}`,
                borderRadius: 20, padding: '3px 9px', whiteSpace: 'nowrap',
              }}>
                <Box style={{
                  width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                  background: isLive ? '#22c55e' : '#f59e0b',
                  boxShadow: isLive ? '0 0 0 2px rgba(34,197,94,0.3)' : '0 0 0 2px rgba(245,158,11,0.3)',
                }} />
                <Typography style={{
                  fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                  color: isLive ? '#16a34a' : '#d97706', lineHeight: 1,
                }}>
                  {isLive ? t(lang, 'liveRate') : t(lang, 'estimatedRate')}
                </Typography>
              </Box>
            )}
          </Box>
          <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Total cost highlight */}
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t(lang, 'totalEstimatedCost')}
              </Typography>
              <Typography style={{ fontSize: '1.3rem', fontWeight: 700, color: '#f59e0b' }}>
                {conv(totalFuelCost)} {cur}
              </Typography>
            </Box>

            <Divider style={{ margin: '2px 0' }} />

            <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{t(lang, 'fuelTypeLbl')}</Typography>
              <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 500 }}>{fuelDef.label}</Typography>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{t(lang, 'consumptionLbl')}</Typography>
              <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 500 }}>
                {totalFuel.toFixed(1)} {fuelDef.unit} × {conv(pricePerUnitEUR, currency.rate >= 100 ? 0 : 3)} {cur}/{fuelDef.unit}
              </Typography>
            </Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{t(lang, 'costPerKm')}</Typography>
              <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 500 }}>
                {conv(costPerKm, currency.rate >= 100 ? 1 : 3)} {cur}/km
              </Typography>
            </Box>
            {co2Kg > 0 && (
              <Box style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>{t(lang, 'co2Emissions')}</Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#22c55e', fontWeight: 500 }}>
                  {co2Kg >= 1000 ? `${(co2Kg / 1000).toFixed(2)} t` : `${co2Kg.toFixed(0)} kg`}
                </Typography>
              </Box>
            )}
          </GlassCard>
        </Box>
      )}

      {/* Toll costs (european_tolls module) */}
      <ModuleGate module="european_tolls">
        <TollsPanel lang={lang} currency={currency} tripMultiplier={tripMultiplier} roadBreakdown={cr?.roadBreakdown} />
      </ModuleGate>

      {/* Custom costs (rubricas) */}
      {distanceKm > 0 && (
        <Box>
          <SectionTitle icon={ReceiptIcon}>{lang === 'pt' ? 'Custos Adicionais' : 'Additional Costs'}</SectionTitle>
          <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* List of existing custom costs */}
            {(route.customCosts || []).map(cost => (
              <Box key={cost.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography style={{ fontSize: '0.72rem', color: '#0f172a', flex: 1 }}>{cost.name}</Typography>
                <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#0f172a', marginRight: 4 }}>
                  {Number(cost.value).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cur}
                </Typography>
                <IconButton size="small" onClick={() => dispatch(removeCustomCost(cost.id))} style={{ padding: 2 }}>
                  <DeleteOutlineIcon style={{ fontSize: 14, color: '#ef4444' }} />
                </IconButton>
              </Box>
            ))}

            {(route.customCosts || []).length > 0 && (
              <Box style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 4 }}>
                <Typography style={{ fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {lang === 'pt' ? 'Subtotal rubricas' : 'Subtotal items'}
                </Typography>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                  {(route.customCosts || []).reduce((s, c) => s + (Number(c.value) || 0), 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cur}
                </Typography>
              </Box>
            )}

            {/* Inline add form */}
            {showCostForm ? (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0', borderTop: '1px dashed #e2e8f0' }}>
                <Select
                  value={costTemplate}
                  onChange={(e) => {
                    setCostTemplate(e.target.value);
                    if (e.target.value && e.target.value !== '__outro__') {
                      setCostName(e.target.value);
                    } else if (e.target.value === '__outro__') {
                      setCostName('');
                    }
                  }}
                  displayEmpty
                  style={{ fontSize: '0.72rem' }}
                  inputProps={{ style: { padding: '6px 8px', fontSize: '0.72rem' } }}
                >
                  <MenuItem value="" disabled style={{ fontSize: '0.72rem' }}>
                    {lang === 'pt' ? 'Selecionar tipo...' : 'Select type...'}
                  </MenuItem>
                  <MenuItem value="Subsídio de refeição" style={{ fontSize: '0.72rem' }}>Subsídio de refeição</MenuItem>
                  <MenuItem value="Subsídio de alojamento" style={{ fontSize: '0.72rem' }}>Subsídio de alojamento</MenuItem>
                  <MenuItem value="Ferry/Travessia" style={{ fontSize: '0.72rem' }}>Ferry/Travessia</MenuItem>
                  <MenuItem value="Estacionamento" style={{ fontSize: '0.72rem' }}>Estacionamento</MenuItem>
                  <MenuItem value="Lavagem" style={{ fontSize: '0.72rem' }}>Lavagem</MenuItem>
                  <MenuItem value="__outro__" style={{ fontSize: '0.72rem' }}>{lang === 'pt' ? 'Outro...' : 'Other...'}</MenuItem>
                </Select>
                {(costTemplate === '__outro__' || (!costTemplate && costName)) && (
                  <TextField
                    value={costName}
                    onChange={(e) => setCostName(e.target.value)}
                    placeholder={lang === 'pt' ? 'Nome da rubrica' : 'Cost name'}
                    size="small"
                    variant="outlined"
                    inputProps={{ style: { padding: '6px 8px', fontSize: '0.72rem' } }}
                    style={{ fontSize: '0.72rem' }}
                  />
                )}
                <TextField
                  value={costValue}
                  onChange={(e) => setCostValue(e.target.value)}
                  placeholder={lang === 'pt' ? 'Valor (€)' : 'Value (€)'}
                  size="small"
                  variant="outlined"
                  type="number"
                  inputProps={{ style: { padding: '6px 8px', fontSize: '0.72rem' }, min: 0, step: 0.01 }}
                  style={{ fontSize: '0.72rem' }}
                />
                <Box style={{ display: 'flex', gap: 6 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!costName.trim() || !costValue}
                    onClick={() => {
                      dispatch(addCustomCost({ name: costName.trim(), value: parseFloat(costValue) || 0 }));
                      setCostName('');
                      setCostValue('');
                      setCostTemplate('');
                      setShowCostForm(false);
                    }}
                    style={{
                      fontSize: '0.68rem', textTransform: 'none', borderRadius: 6, padding: '3px 12px',
                      background: (!costName.trim() || !costValue) ? '#e2e8f0' : '#3b82f6', color: '#fff',
                      boxShadow: 'none',
                    }}
                  >
                    {lang === 'pt' ? 'Guardar' : 'Save'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => { setShowCostForm(false); setCostName(''); setCostValue(''); setCostTemplate(''); }}
                    style={{ fontSize: '0.68rem', textTransform: 'none', borderRadius: 6, padding: '3px 12px', color: '#64748b' }}
                  >
                    {lang === 'pt' ? 'Cancelar' : 'Cancel'}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon style={{ fontSize: 14 }} />}
                onClick={() => setShowCostForm(true)}
                style={{
                  fontSize: '0.68rem', textTransform: 'none', color: '#3b82f6',
                  borderRadius: 6, padding: '3px 8px', justifyContent: 'flex-start',
                }}
              >
                {lang === 'pt' ? 'Adicionar Rubrica' : 'Add Cost Item'}
              </Button>
            )}
          </GlassCard>
        </Box>
      )}

      {/* Total trip cost summary (fuel + tolls + custom costs) */}
      {distanceKm > 0 && (() => {
        // Combustível efectivo — conta única partilhada com o resumo de viagem
        // (effectiveFuelCostEUR), por país via resolveFuelPriceEUR. Garante que
        // os cards e o resumo nunca divergem (ver src/utils/resolveFuelPrice.js).
        const fuelCostEUR = effectiveFuelCostEUR;

        // Aplicar overrides por país (clique inline no TollsPanel) ao
        // total — mantém os 2 cards (TollsPanel + Custo total da viagem)
        // sincronizados quando o operador ajusta o GBR/Irlanda inflados
        // pela HERE.
        let tollCostBase = tollData?.totalCost || 0;
        if (tollData?.tolls && Object.keys(tollOverrides || {}).length > 0) {
          const byCC = {};
          tollData.tolls.forEach(t => {
            const cc = t.countryCode || 'OTHER';
            byCC[cc] = (byCC[cc] || 0) + (t.cost || 0);
          });
          tollCostBase = Object.entries(byCC).reduce((s, [cc, v]) => {
            return s + (tollOverrides[cc] != null ? tollOverrides[cc] : v);
          }, 0);
        }
        const tollCostEUR = tollCostBase * tripMultiplier;
        const hasTolls = tollData && !tollLoading && tollCostEUR > 0;
        // Custom costs are already in display currency — convert to EUR for consistent conv() usage
        const customCostsTotal = (route.customCosts || []).reduce((s, c) => s + (Number(c.value) || 0), 0);
        const customCostsTotalEUR = customCostsTotal / currency.rate;
        const hasCustomCosts = customCostsTotal > 0;
        const totalTripEUR = fuelCostEUR + tollCostEUR + customCostsTotalEUR;

        // Only show if we have at least fuel cost calculated
        if (!fuelCostEUR && !hasTolls && !hasCustomCosts) return null;

        return (
          <Box>
            <SectionTitle icon={AccountBalanceWalletIcon}>{t(lang, 'totalTripCost')}</SectionTitle>
            <GlassCard flat padding="sm" style={{
              display: 'flex', flexDirection: 'column', gap: 6,
              background: 'linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(245,158,11,0.06) 100%)',
              border: '1px solid rgba(99,102,241,0.15)',
            }}>
              {/* Fuel line */}
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <LocalGasStationIcon style={{ fontSize: 14, color: '#f59e0b' }} />
                  <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    {t(lang, 'fuel')}
                  </Typography>
                </Box>
                <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#f59e0b' }}>
                  {conv(fuelCostEUR)} {cur}
                </Typography>
              </Box>

              {/* Tolls line */}
              {hasTolls && (
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <MonetizationOnIcon style={{ fontSize: 14, color: '#8b5cf6' }} />
                    <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
                      {t(lang, 'tollCosts')}
                    </Typography>
                  </Box>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#8b5cf6' }}>
                    {conv(tollCostEUR)} {cur}
                  </Typography>
                </Box>
              )}

              {/* Tolls loading */}
              {tollLoading && (
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CircularProgress size={12} />
                    <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      {t(lang, 'tollsLoading')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Custom costs line */}
              {hasCustomCosts && (
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ReceiptIcon style={{ fontSize: 14, color: '#0ea5e9' }} />
                    <Typography style={{ fontSize: '0.72rem', color: '#64748b' }}>
                      {lang === 'pt' ? 'Custos adicionais' : 'Additional costs'}
                      <span style={{ fontSize: '0.58rem', color: '#94a3b8', marginLeft: 4 }}>
                        ({(route.customCosts || []).length} {(route.customCosts || []).length === 1 ? 'rubrica' : 'rubricas'})
                      </span>
                    </Typography>
                  </Box>
                  <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#0ea5e9' }}>
                    {conv(customCostsTotalEUR)} {cur}
                  </Typography>
                </Box>
              )}

              <Divider style={{ margin: '2px 0' }} />

              {/* Total */}
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Total
                </Typography>
                <Typography style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
                  {conv(totalTripEUR)} {cur}
                </Typography>
              </Box>

              {/* Cost per km */}
              <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                  {conv(totalTripEUR / distanceKm, currency.rate >= 100 ? 1 : 3)} {cur}/km
                </Typography>
              </Box>
            </GlassCard>

            {/* Custo do Transporte — operator types this in the display currency
                (Kz for Angola, € for Portugal, etc). Everything else on this panel
                is in EUR internally, so we convert to EUR up front for margin math
                and use `conv()` only to render the already-EUR values. */}
            {route.estimatedCost != null && route.estimatedCost !== '' && Number(route.estimatedCost) > 0 && (() => {
              const transportValueLocal = Number(route.estimatedCost);
              const transportValueEUR = transportValueLocal / currency.rate;
              const marginEUR = transportValueEUR - totalTripEUR;
              const marginPct = totalTripEUR > 0 ? (marginEUR / totalTripEUR) * 100 : 0;
              const isProfit = marginEUR >= 0;
              return (
                <GlassCard flat padding="sm" style={{
                  marginTop: 8,
                  background: 'linear-gradient(135deg, rgba(5,150,105,0.08) 0%, rgba(16,185,129,0.04) 100%)',
                  border: '1px solid rgba(5,150,105,0.2)',
                }}>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <EuroSymbolIcon style={{ fontSize: 14, color: '#059669' }} />
                      <Typography style={{ fontSize: '0.72rem', color: '#065f46', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {lang === 'pt' ? 'Custo do Transporte' : 'Transport Cost'}
                      </Typography>
                    </Box>
                    <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#059669' }}>
                      {conv(transportValueEUR)} {cur}
                    </Typography>
                  </Box>
                  <Typography style={{ fontSize: '0.58rem', color: '#64748b', marginTop: 2 }}>
                    {lang === 'pt' ? 'Valor acordado com o cliente' : 'Value agreed with customer'}
                  </Typography>
                  {totalTripEUR > 0 && (
                    <Box style={{
                      marginTop: 6,
                      padding: '4px 8px',
                      background: isProfit ? 'rgba(5,150,105,0.08)' : 'rgba(239,68,68,0.08)',
                      border: `1px solid ${isProfit ? 'rgba(5,150,105,0.2)' : 'rgba(239,68,68,0.2)'}`,
                      borderRadius: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}>
                      <Typography style={{ fontSize: '0.6rem', color: isProfit ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                        {lang === 'pt' ? (isProfit ? 'Margem estimada' : 'Prejuízo estimado') : (isProfit ? 'Estimated margin' : 'Estimated loss')}
                      </Typography>
                      <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: isProfit ? '#059669' : '#dc2626' }}>
                        {isProfit ? '+' : ''}{conv(marginEUR)} {cur} ({isProfit ? '+' : ''}{marginPct.toFixed(1)}%)
                      </Typography>
                    </Box>
                  )}
                </GlassCard>
              );
            })()}
          </Box>
        );
      })()}

      {/* Alternative routes */}
      {route.alternatives?.length > 0 && (
        <Box>
          <SectionTitle>{t(lang, 'alternativeRoutes')}</SectionTitle>
          <Box style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {route.alternatives.map((alt, idx) => {
              const altKm = (alt.distance / 1000).toFixed(1);
              const altMin = Math.round(alt.duration / 60);
              const altH = Math.floor(altMin / 60);
              const altM = altMin % 60;
              const altCostEUR = (alt.distance / 1000 / 100) * consumption * pricePerUnitEUR;
              return (
                <GlassCard key={idx} flat padding="sm"
                  style={{ borderLeft: '3px solid #94a3b8', display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {t(lang, 'alternative')} {idx + 1}
                  </Typography>
                  <Box style={{ display: 'flex', gap: 12 }}>
                    <Box>
                      <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>{t(lang, 'distance')}</Typography>
                      <Typography style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>{altKm} km</Typography>
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>{t(lang, 'time')}</Typography>
                      <Typography style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>{altH}h {altM}m</Typography>
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>{t(lang, 'fuel')}</Typography>
                      <Typography style={{ fontSize: '0.88rem', fontWeight: 600, color: '#f59e0b' }}>{conv(altCostEUR)} {cur}</Typography>
                    </Box>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ fontSize: '0.7rem', marginTop: 2, borderColor: 'rgba(59,130,246,0.4)', color: '#3b82f6' }}
                    onClick={() => {
                      // Mesmo gate da computação principal — só geramos
                      // paragens de tacógrafo se a rota actual está
                      // explicitamente configurada para isso (e nunca em
                      // ligeiros). Sem este check, clicar numa alternativa
                      // reaparecia P1/P2 no mapa apesar de a preferência
                      // do cliente / preset do veículo ser "sem tacho".
                      const isLight = route.routeOptions?.vehicleType === 'car';
                      const useTacho = !isLight && route.routeOptions?.useTachograph === true;
                      const stops = useTacho
                        ? calculateStops(alt.duration, alt.distance, route.departureTime)
                        : [];
                      const remaining = route.alternatives.filter((_, i) => i !== idx);
                      dispatch(setRouteResult({ computedRoute: alt, stops, alternatives: [route.computedRoute, ...remaining] }));
                    }}
                  >
                    {t(lang, 'useThisRoute')}
                  </Button>
                </GlassCard>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Road distribution panel (ViaMichelin-style) */}
      {cr?.roadBreakdown && (() => {
        const rb = cr.roadBreakdown;
        const totalM = Object.values(rb).reduce((s, v) => s + v, 0);
        if (totalM < 100) return null;
        const totalKm = (totalM / 1000) * tripMultiplier;
        const durationSec = cr.duration * tripMultiplier;
        const entries = Object.entries(ROAD_LABEL_KEYS)
          .map(([type, cfg]) => {
            const m = rb[type] || 0;
            const pct = totalM > 0 ? (m / totalM) * 100 : 0;
            const km = (m / 1000) * tripMultiplier;
            const mins = durationSec > 0 ? Math.round((pct / 100) * (durationSec / 60)) : 0;
            return { type, ...cfg, m, pct, km, mins };
          })
          .filter(e => e.pct >= 0.5)
          .sort((a, b) => b.pct - a.pct);
        return (
          <Box>
            <SectionTitle icon={TimelineIcon}>{t(lang, 'roadBreakdownLabel')}</SectionTitle>
            <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Stacked bar */}
              <Box style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: '#f1f5f9' }}>
                {entries.map(e => (
                  <Box key={e.type} style={{ width: `${e.pct}%`, background: e.color, transition: 'width 0.3s' }}
                    title={`${t(lang, e.key)}: ${e.pct.toFixed(0)}%`}
                  />
                ))}
              </Box>
              {/* Rows */}
              {entries.map(e => (
                <Box key={e.type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Box style={{ width: 10, height: 10, borderRadius: 2, background: e.color, flexShrink: 0 }} />
                  <Typography style={{ fontSize: '0.72rem', color: '#0f172a', flex: 1 }}>
                    {t(lang, e.key)}
                  </Typography>
                  <Typography style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500, textAlign: 'right' }}>
                    {e.km.toFixed(1)} km
                  </Typography>
                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', width: 55, textAlign: 'right' }}>
                    {e.mins >= 60 ? `${Math.floor(e.mins / 60)}h ${e.mins % 60}min` : `${e.mins}min`}
                  </Typography>
                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, width: 30, textAlign: 'right' }}>
                    {e.pct.toFixed(0)}%
                  </Typography>
                </Box>
              ))}
              {/* Total */}
              <Divider style={{ margin: '2px 0' }} />
              <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Box style={{ width: 10, height: 10, flexShrink: 0 }} />
                <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 600, flex: 1 }}>
                  {t(lang, 'roadBreakdownTotal')}
                </Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#0f172a', fontWeight: 600, textAlign: 'right' }}>
                  {totalKm.toFixed(1)} km
                </Typography>
                <Typography style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 500, width: 55, textAlign: 'right' }}>
                  {durationSec >= 3600 ? `${Math.floor(durationSec / 3600)}h ${Math.round((durationSec % 3600) / 60)}min` : `${Math.round(durationSec / 60)}min`}
                </Typography>
                <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, width: 30, textAlign: 'right' }}>
                  100%
                </Typography>
              </Box>
              {/* Warning note when avoid conditions are active but route still uses those road types */}
              {(() => {
                const warnings = [];
                if (opts.avoidMotorways && (rb.motorway || 0) > 100) {
                  warnings.push(lang === 'pt'
                    ? `Inclui ${((rb.motorway || 0) / 1000).toFixed(1)} km de autoestrada — sem alternativa viável`
                    : `Includes ${((rb.motorway || 0) / 1000).toFixed(1)} km of motorway — no viable alternative`);
                }
                if (opts.avoidTolls && (rb.motorway || 0) > 100) {
                  warnings.push(lang === 'pt'
                    ? 'Alguns troços podem ter portagem onde não existe alternativa'
                    : 'Some sections may have tolls where no alternative exists');
                }
                if (warnings.length === 0) return null;
                return (
                  <Box style={{ marginTop: 4, padding: '6px 8px', borderRadius: 6, background: '#fffbeb', border: '1px solid #fde68a' }}>
                    {warnings.map((w, i) => (
                      <Typography key={i} style={{ fontSize: '0.6rem', color: '#92400e', lineHeight: 1.4 }}>
                        ⚠ {w}
                      </Typography>
                    ))}
                  </Box>
                );
              })()}
            </GlassCard>
          </Box>
        );
      })()}

      {/* Full route street list — timeline style */}
      {route.routeSteps?.length > 0 && (
        <Box>
          <Box
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
            onClick={() => { setShowSteps(v => !v); if (showSteps) dispatch(setHighlightedStepIdx(null)); }}
          >
            <SectionTitle icon={ListIcon}>{t(lang, 'fullRoute')}</SectionTitle>
            <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                {route.routeSteps.length} {t(lang, 'segments')}
              </Typography>
              {showSteps ? <ExpandLessIcon style={{ fontSize: 18, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 18, color: '#94a3b8' }} />}
            </Box>
          </Box>
          <Collapse in={showSteps}>
            <GlassCard flat padding="sm" style={{ maxHeight: 440, overflowY: 'auto', paddingLeft: 4 }}>
              {(() => {
                let accKm = 0;
                let lastCountry = '';
                return route.routeSteps.map((step, idx) => {
                  const cfg = ROAD_LABEL_KEYS[step.type] || ROAD_LABEL_KEYS.local;
                  const stepKm = (step.distance / 1000).toFixed(1);
                  accKm += step.distance / 1000;
                  const durMin = step.duration ? Math.round(step.duration / 60) : Math.round(step.distance / 1000 / 1.1);
                  const durH = Math.floor(durMin / 60);
                  const durM = durMin % 60;
                  const durStr = durH > 0 ? `${durH}h${durM > 0 ? durM + 'm' : ''}` : `${durM}m`;
                  const isActive = highlightedStepIdx === idx;
                  const isLast = idx === route.routeSteps.length - 1;
                  const showCountry = step.country && step.country !== lastCountry;
                  if (step.country) lastCountry = step.country;

                  return (
                    <React.Fragment key={idx}>
                      {/* Country separator */}
                      {showCountry && (
                        <Box style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px 2px 24px' }}>
                          <Box style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                          <Typography style={{ fontSize: '0.52rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {step.country}
                          </Typography>
                          <Box style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                        </Box>
                      )}
                      <Box
                        onClick={() => dispatch(setHighlightedStepIdx(isActive ? null : idx))}
                        style={{
                          display: 'flex', gap: 10, cursor: 'pointer', position: 'relative',
                          padding: '7px 8px 7px 4px', marginLeft: 8, borderRadius: 8,
                          background: isActive ? `${cfg.color}10` : 'transparent',
                          border: isActive ? `1px solid ${cfg.color}30` : '1px solid transparent',
                          transition: 'all 0.15s',
                        }}
                      >
                        {/* Timeline line + dot */}
                        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 14, flexShrink: 0 }}>
                          <Box style={{
                            width: isActive ? 10 : 8, height: isActive ? 10 : 8, borderRadius: '50%',
                            background: isActive ? cfg.color : '#fff',
                            border: `2.5px solid ${cfg.color}`,
                            flexShrink: 0, zIndex: 1, transition: 'all 0.15s',
                            boxShadow: isActive ? `0 0 0 3px ${cfg.color}20` : 'none',
                          }} />
                          {!isLast && (
                            <Box style={{
                              width: 2, flex: 1, minHeight: 20,
                              background: `linear-gradient(${cfg.color}80, ${(ROAD_LABEL_KEYS[route.routeSteps[idx + 1]?.type] || ROAD_LABEL_KEYS.local).color}80)`,
                            }} />
                          )}
                        </Box>

                        {/* Content */}
                        <Box style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : 2 }}>
                          {/* Row 1: road name + full name */}
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Typography style={{
                              fontSize: '0.72rem', color: isActive ? cfg.color : '#0f172a',
                              fontWeight: step.ref ? 700 : 500,
                              overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
                              whiteSpace: isActive ? 'normal' : 'nowrap',
                            }}>
                              {step.ref && <span style={{ fontWeight: 700 }}>{step.ref}</span>}
                              {step.name && step.name !== step.ref && (
                                <span style={{ fontWeight: 400, color: isActive ? '#475569' : '#64748b', fontSize: '0.66rem' }}> {step.name}</span>
                              )}
                              {!step.ref && !step.name && (step.label || t(lang, 'unnamed'))}
                            </Typography>
                          </Box>
                          {/* Instruction detail (shown when active) */}
                          {isActive && step.instruction && (
                            <Typography style={{ fontSize: '0.6rem', color: '#64748b', marginTop: 2, lineHeight: 1.3, fontStyle: 'italic' }}>
                              {step.instruction}
                            </Typography>
                          )}
                          {/* Row 2: type badge + km + duration + speed + accumulated */}
                          <Box style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                            <Box style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              background: `${cfg.color}12`, border: `1px solid ${cfg.color}25`,
                              borderRadius: 10, padding: '1px 6px',
                            }}>
                              <Box style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.color }} />
                              <Typography style={{ fontSize: '0.5rem', color: cfg.color, fontWeight: 700, letterSpacing: '0.03em' }}>
                                {t(lang, cfg.key)}
                              </Typography>
                            </Box>
                            <Typography style={{ fontSize: '0.56rem', color: '#475569', fontWeight: 600 }}>
                              {stepKm} km
                            </Typography>
                            <Typography style={{ fontSize: '0.53rem', color: '#94a3b8' }}>
                              ~{durStr}
                            </Typography>
                            {durMin > 0 && Number(stepKm) > 0 && (
                              <Typography style={{ fontSize: '0.5rem', color: '#06b6d4', fontWeight: 600 }}>
                                {Math.round(Number(stepKm) / (durMin / 60))} km/h
                              </Typography>
                            )}
                            {(step.type === 'motorway') && (
                              <Typography style={{ fontSize: '0.46rem', color: '#f59e0b', fontWeight: 700, background: '#fef3c7', borderRadius: 4, padding: '0 4px' }}>
                                PORTAGEM
                              </Typography>
                            )}
                            <Typography style={{ fontSize: '0.48rem', color: '#cbd5e1', marginLeft: 'auto' }}>
                              km {accKm.toFixed(0)}
                            </Typography>
                            {isActive && (
                              <Typography style={{ fontSize: '0.48rem', color: '#f43f5e', fontWeight: 700 }}>
                                ● {t(lang, 'showOnMap')}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </React.Fragment>
                  );
                });
              })()}
            </GlassCard>
          </Collapse>
        </Box>
      )}

      <Divider />

      {/* Tachograph breakdown (only when tachograph enabled) */}
      {route.routeOptions?.useTachograph !== false && (
        <TachographPanel route={route} lang={lang} />
      )}

      {/* Route endpoints */}
      <Box>
        <SectionTitle>{t(lang, 'journey')}</SectionTitle>
        <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Box style={{ display: 'flex', gap: 8 }}>
            <Box style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', marginTop: 5, flexShrink: 0 }} />
            <Box>
              <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t(lang, 'origin')}</Typography>
              <Typography style={{ fontSize: '0.78rem', color: '#0f172a' }}>{route.origin?.name || '—'}</Typography>
            </Box>
          </Box>
          {(route.waypoints || []).map((wp, i) => wp.name ? (
            <Box key={i} style={{ display: 'flex', gap: 8, paddingLeft: 0 }}>
              <Box style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', marginTop: 5, flexShrink: 0 }} />
              <Box>
                <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t(lang, 'via')} {i + 1}</Typography>
                <Typography style={{ fontSize: '0.78rem', color: '#0f172a' }}>{wp.name}</Typography>
              </Box>
            </Box>
          ) : null)}
          <Box style={{ display: 'flex', gap: 8 }}>
            <Box style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', marginTop: 5, flexShrink: 0 }} />
            <Box>
              <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t(lang, 'destination')}</Typography>
              <Typography style={{ fontSize: '0.78rem', color: '#0f172a' }}>{route.destination?.name || '—'}</Typography>
            </Box>
          </Box>
        </GlassCard>
      </Box>

      {/* Vehicle/Driver summary */}
      {(vehicle || driver) && (
        <Box>
          <SectionTitle>{t(lang, 'assignment')}</SectionTitle>
          <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {vehicle && (
              <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{t(lang, 'vehicle')}</Typography>
                <Typography style={{ fontSize: '0.75rem', color: '#3b82f6', fontWeight: 600 }}>{vehicle.plate}</Typography>
              </Box>
            )}
            {driver && (
              <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{t(lang, 'driver')}</Typography>
                <Typography style={{ fontSize: '0.75rem', color: '#0f172a' }}>{driver.name}</Typography>
              </Box>
            )}
          </GlassCard>
        </Box>
      )}

      {/* Planned vs Actual comparison — percurso real do veículo,
          legenda e métricas (distância, desvios, duração). */}
      {actualTrail?.trail?.length > 1 && cr && (() => {
        const trail = actualTrail.trail;
        const a = analyzeTrail(trail, cr.geometry?.coordinates);
        const plannedKm = cr.distance / 1000;
        const desvioKm = Math.abs(a.realKm - plannedKm);
        const desvioPct = plannedKm > 0 ? (desvioKm / plannedKm) * 100 : 0;
        const fmtDur = (m) => (m == null ? '—'
          : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}min` : `${m} min`);
        const row = (label, value, color) => (
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{label}</Typography>
            <Typography style={{ fontSize: '0.78rem', color: color || '#0f172a', fontWeight: 600 }}>{value}</Typography>
          </Box>
        );
        return (
          <Box>
            <SectionTitle>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Box style={{ width: 10, height: 3, borderRadius: 2, background: '#22c55e' }} />
                Percurso real vs planeado
              </Box>
            </SectionTitle>
            <GlassCard flat padding="sm" style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {/* Legenda das linhas do mapa */}
              <Box style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {[
                  { c: '#2563eb', t: 'Rota planeada' },
                  { c: '#16a34a', t: 'Percurso real' },
                  { c: '#dc2626', t: 'Desvio da rota' },
                ].map(l => (
                  <Box key={l.t} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Box style={{ width: 16, height: 4, borderRadius: 2, background: l.c, flexShrink: 0 }} />
                    <Typography style={{ fontSize: '0.62rem', color: '#475569', fontWeight: 600 }}>{l.t}</Typography>
                  </Box>
                ))}
              </Box>
              <Divider style={{ margin: '2px 0' }} />
              {row('Dist. planeada', `${plannedKm.toFixed(1)} km`, '#3b82f6')}
              {row('Dist. real', `${a.realKm.toFixed(1)} km`, '#22c55e')}
              {row('Desvio total', `${desvioKm.toFixed(1)} km (${desvioPct.toFixed(1)}%)`,
                desvioPct > 10 ? '#ef4444' : desvioPct > 5 ? '#f59e0b' : '#22c55e')}
              <Divider style={{ margin: '2px 0' }} />
              {row('Fora da rota',
                a.offCount > 0 ? `${a.offCount} ${a.offCount === 1 ? 'desvio' : 'desvios'} · ${a.offKm.toFixed(1)} km` : 'nenhum',
                a.offCount > 0 ? '#dc2626' : '#22c55e')}
              {a.durMin != null && row('Duração', fmtDur(a.durMin))}
              <Typography style={{ fontSize: '0.55rem', color: '#94a3b8', marginTop: 2 }}>
                {trail.length} pontos GPS registados
              </Typography>
            </GlassCard>
          </Box>
        );
      })()}

      {/* Dialog fullscreen — resumo visual da rota com PDF */}
      <RouteSummaryDialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        route={route}
        fuelData={{
          totalCostEUR: effectiveFuelCostEUR || 0,
          currencyCode: currency?.code || 'EUR',
          currencySymbol: currency?.symbol || currency?.code || '€',
          currencyRate: currency?.rate || 1,
          countryRows: [],
        }}
        lang={lang}
      />
    </Box>
  );
}

export default RouteDetails;
